import { useEffect, useState } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { cn } from "@/lib/utils";
import { useReducedMotion } from "@/hooks/use-reduced-motion";

interface ScrollProgressIndicatorProps {
  /** Height of the progress bar */
  height?: number;
  /** Custom className */
  className?: string;
  /** Show only on specific routes (if empty, shows on all) */
  showOnRoutes?: string[];
  /** Z-index for the progress bar */
  zIndex?: number;
}

export function ScrollProgressIndicator({
  height = 3,
  className,
  zIndex = 50,
}: ScrollProgressIndicatorProps) {
  const [isVisible, setIsVisible] = useState(false);
  const prefersReducedMotion = useReducedMotion();
  
  const { scrollYProgress } = useScroll();
  
  // Smooth spring animation for the progress bar
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  useEffect(() => {
    let rafId: number | null = null;
    const handleScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        setIsVisible(window.scrollY > 50);
        rafId = null;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  // Use direct scrollYProgress for reduced motion (no spring animation)
  const progress = prefersReducedMotion ? scrollYProgress : scaleX;

  return (
    <motion.div
      className={cn(
        "fixed top-0 left-0 right-0 origin-left bg-primary",
        className
      )}
      style={{
        scaleX: progress,
        height,
        zIndex,
        opacity: isVisible ? 1 : 0,
        transition: "opacity 0.2s ease-out",
      }}
      aria-hidden="true"
    />
  );
}

export default ScrollProgressIndicator;
